// src/prototype/artifacts/renderers/ArtifactSectionRenderer.tsx
// ============================================================
// Renders a single DvaArtifactSection with all its blocks.
// Dispatches each block to the correct block renderer.
// ============================================================

import React from 'react';
import { DvaArtifactSection, DvaArtifactBlock } from '../types/artifactTypes';
import {
  ParagraphBlock,
  CalloutBlock,
  StatGroupBlock,
  BulletListBlock,
  TableBlock,
  TimelinePhaseBlock,
  RiskItemBlock,
  AssumptionItemBlock,
  TagRowBlock,
} from './blockRenderers';
import { formatPlainExecutiveText } from '../../utils/executiveCopy';

// ── Block dispatcher ──────────────────────────────────────────

const BlockRenderer: React.FC<{
  block: DvaArtifactBlock;
  blockIndex: number;
  totalBlocks: number;
}> = ({ block, blockIndex, totalBlocks }) => {
  switch (block.blockType) {
    case 'paragraph':
      return <ParagraphBlock block={block} />;

    case 'callout':
      return <CalloutBlock block={block} />;

    case 'stat':
    case 'stat_group':
      return <StatGroupBlock block={block} />;

    case 'bullet_list':
      return <BulletListBlock block={block} />;

    case 'table':
      return <TableBlock block={block} />;

    case 'timeline_phase':
      return (
        <TimelinePhaseBlock
          block={block}
          phaseNumber={blockIndex + 1}
          isLast={blockIndex === totalBlocks - 1}
        />
      );

    case 'risk_item':
      return <RiskItemBlock block={block} />;

    case 'assumption_item':
      return <AssumptionItemBlock block={block} />;

    case 'tag_row':
      return <TagRowBlock block={block} />;

    default:
      return (
        <p className="text-xs text-[#717182] italic">
          [Unsupported block type: {block.blockType}]
        </p>
      );
  }
};

// ── Section renderer ──────────────────────────────────────────

interface ArtifactSectionRendererProps {
  section: DvaArtifactSection;
}

export const ArtifactSectionRenderer: React.FC<ArtifactSectionRendererProps> = ({ section }) => {
  if (!section.blocks || section.blocks.length === 0) return null;

  return (
    <div data-export-block className="bg-white rounded-xl border border-[#e5e7eb] shadow-sm overflow-hidden">
      {/* Section header */}
      <div className="px-4 py-3 border-b border-[#e5e7eb] bg-white">
        <h3 className="text-sm font-semibold text-[#18365E]">
          {formatPlainExecutiveText(section.title)}
        </h3>
        {section.subtitle && (
          <p className="text-[10px] text-[#717182] mt-0.5">
            {formatPlainExecutiveText(section.subtitle)}
          </p>
        )}
      </div>

      {/* Section blocks */}
      <div className="p-4 space-y-3">
        {section.blocks.map((block, i) => (
          <BlockRenderer
            key={block.blockId}
            block={block}
            blockIndex={i}
            totalBlocks={section.blocks.length}
          />
        ))}
      </div>
    </div>
  );
};